import { store } from './store.js';
// ========================================
// PASO 5: CÁLCULO
// ========================================
import { wizardState, getString, isSubtraction } from './wizardState.js';
import { safeSetHTML } from './security.js';

// Estado local
let calculationTableComplete = false;
let numberSelectorComplete = false;
let currentMentalValue = 0; // Para el método 3

export function initializeCalculationStep() {
    if (!store.state || !store.state.problems) return;

    // Reset local state
    calculationTableComplete = false;
    numberSelectorComplete = false;
    currentMentalValue = 0;

    // Clear previous inputs
    const input = document.getElementById('finalResultInput');
    const selectorDisplay = document.getElementById('selectedNumberDisplay');

    if (input) input.value = '';
    if (selectorDisplay) {
        selectorDisplay.textContent = '?';
        selectorDisplay.dataset.value = '';
    }

    // Reset Mental Calc Display
    const mentalDisplay = document.getElementById('selectedCalcNumberDisplay');
    if (mentalDisplay) mentalDisplay.textContent = '?';
    const validateCalcBtn = document.getElementById('validateCalcBtn');
    if (validateCalcBtn) validateCalcBtn.disabled = true;

    // --- FIX: POPULATE SHARED DATA FOR ALL METHODS ---
    const problem = store.state.problems[store.state.currentProblem];
    if (problem) {
        // Ensure consistent operand ordering (Largest first usually, or specific to problem type)
        let num1 = 0;
        let num2 = 0;
        if (problem.data && problem.data.useful) {
            num1 = Math.max(...problem.data.useful);
            num2 = Math.min(...problem.data.useful);
        }

        const opSymbol = isSubtraction(problem) ? '-' : '+';

        // Populate DOM elements used by Method 2 (ABN Button) and Method 3 (Mental Calc)
        const elNum1 = document.getElementById('calcNum1');
        const elNum2 = document.getElementById('calcNum2');
        const elOp = document.getElementById('calcOperator');

        const colorize = (n) => {
            const str = n.toString();
            if (str.length === 1) return `<span style="color: #1e40af;">${str}</span>`;
            const divTens = `<span style="color: #be123c;">${str.slice(0, -1)}</span>`;
            const divUnits = `<span style="color: #1e40af;">${str.slice(-1)}</span>`;
            return divTens + divUnits;
        };

        if (elNum1) safeSetHTML(elNum1, colorize(num1));
        if (elNum2) safeSetHTML(elNum2, colorize(num2));
        if (elOp) elOp.textContent = opSymbol;
    }
    // -------------------------------------------------

    // Inicializar visualización del método seleccionado
    const method = store.config && store.config.method ? store.config.method : 'abn';

    // METHOD 1: Generar tabla vertical
    const grid = document.getElementById('calcTable');
    if (grid) {
        // CLEANUP: Always clear first to remove previous problem data
        safeSetHTML(grid, '');
        grid.removeAttribute('style');
        renderVerticalCalcTable(grid);
    }

    // --- FORCE HIGH CONTRAST TITLES (NUCLEAR OPTION) ---
    // Wait slightly for DOM updates if any, then force styles
    setTimeout(() => {
        const titles = document.querySelectorAll('.method-title');
        titles.forEach(el => {
            el.style.cssText = 'color: #0f172a !important; font-weight: 800 !important; text-shadow: none !important; opacity: 1 !important; font-family: var(--font-primary, sans-serif) !important;';
        });

        // Configurar eventos de mantener pulsado para el spinner (Método 3)
        setupSpinnerHoldEvents();
    }, 100);

    Logger.info('🧮 Paso de cálculo inicializado. Método:', method);
}

// === SPINNER HOLD EVENTS HELPERS ===
let spinnerInterval = null;
const SPINNER_SPEED = 200; // Velocidad moderada para niños

function setupSpinnerHoldEvents() {
    const btnUp = document.querySelector('.spinner-btn.up');
    const btnDown = document.querySelector('.spinner-btn.down');

    const clearSpinnerInterval = () => {
        if (spinnerInterval) {
            clearInterval(spinnerInterval);
            spinnerInterval = null;
        }
    };

    const startSpinning = (delta) => {
        if (spinnerInterval) return; // Evitar duplicados
        // adjustCalcNumber ya hace la acción individual.
        // Si es mousedown, queremos que NO se dispare el onclick nativo dos veces si es rápido, 
        // pero adjustCalcNumber es seguro de llamar. 
        // Sin embargo, como el onclick está en el HTML, se disparará al soltar el click.
        // Para evitar doble salto al hacer un click simple (mousedown -> interval -> mouseup -> click),
        // podríamos quitar el onclick del HTML, pero es invasivo.
        // Solución simple: El intervalo empieza DESPUÉS de un pequeño delay (long press) o 
        // asumimos que el usuario verá avanzar los números.

        // Mejor aproximación: Ejecutar inmediatamente y luego intervalo.
        // El onclick del HTML se disparará al soltar. Eso sumaría uno extra.
        // Idealmente deberíamos remover el onclick del HTML dinámicamente o manejar todo aquí.
        // Vamos a manejar todo aquí y quitar el onclick inline si es posible, o vivir con el +1 extra en click rápido.
        // Dado que el usuario pidió "mantener pulsado", asumo que el click simple sigue funcionando.

        // ESTRATEGIA: Usar el intervalo. El primer 'ajuste' lo hace el evento click normal si es rápido.
        // Si se mantiene, el intervalo arranca.

        spinnerInterval = setInterval(() => {
            adjustCalcNumber(delta);
        }, SPINNER_SPEED);
    };

    [btnUp, btnDown].forEach(btn => {
        if (!btn) return;

        // Clonar nodo para eliminar listeners anteriores si los hubiera y evitar duplicados
        // O simplemente reasignar.

        // Determinar dirección
        const delta = btn.classList.contains('up') ? 1 : -1;

        // Limpiar eventos previos para no acumular
        btn.onmousedown = null;
        btn.onmouseup = null;
        btn.onmouseleave = null;
        btn.ontouchstart = null;
        btn.ontouchend = null;

        // Asignar nuevos
        btn.onmousedown = () => startSpinning(delta);
        btn.onmouseup = clearSpinnerInterval;
        btn.onmouseleave = clearSpinnerInterval;

        // Touch
        btn.ontouchstart = (e) => {
            // e.preventDefault(); // Podría bloquear el click, cuidado.
            startSpinning(delta);
        };
        btn.ontouchend = clearSpinnerInterval;
    });
}


// === METHOD 1: VERTICAL TABLE RESTORED (Dotted Style + COLORS) ===
function renderVerticalCalcTable(table) {
    const problem = store.state.problems[store.state.currentProblem];
    if (!problem) return;

    let num1 = 0;
    let num2 = 0;
    if (problem.data && problem.data.useful) {
        num1 = Math.max(...problem.data.useful);
        num2 = Math.min(...problem.data.useful);
    }
    const isSub = isSubtraction(problem);

    // Helper: split digits
    const getTens = n => Math.floor(n / 10);
    const getUnits = n => n % 10;

    // Styling logic: mimic the dashed box look
    const dashedCellStyle = `
        width: 85px; 
        height: 80px; 
        border: 2px dashed #94a3b8; 
        border-radius: 12px; 
        display: flex; 
        align-items: center; 
        justify-content: center; 
        font-size: 2rem; 
        font-weight: 800;
        background: white;
        margin: 4px;
    `;

    // RED for Tens (#be123c), BLUE for Units (#1e40af)
    const styleTens = `color: #be123c;`;
    const styleUnits = `color: #1e40af;`;

    const headerStyle = `
        width: 85px; 
        text-align: center; 
        font-weight: 800; 
        font-size: 1.5rem; 
        margin-bottom: 4px;
    `;

    // Clear and set flex container
    table.innerHTML = '';
    table.style.display = 'flex';
    table.style.flexDirection = 'column';
    table.style.alignItems = 'center';
    table.style.gap = '10px';
    table.style.background = 'white';
    table.style.padding = '20px';
    table.style.borderRadius = '20px';
    table.style.boxShadow = '0 10px 25px rgba(0,0,0,0.1)';

    // Container for the grid (Headers + Numbers)
    const gridContainer = document.createElement('div');
    gridContainer.style.display = 'grid';
    gridContainer.style.gridTemplateColumns = 'auto auto auto'; // Operator | Tens | Units
    gridContainer.style.gap = '10px';
    gridContainer.style.alignItems = 'center';

    let gridHTML = '';

    // Check if carrying is needed (for addition only)
    const unitsSum = getUnits(num1) + getUnits(num2);
    const hasCarry = !isSub && unitsSum >= 10;

    // Robust Expert Level Check: handles '3', 3, 'expert', 'Genio', etc.
    const rawConfigLevel = store.config ? String(store.config.level).toLowerCase() : '';
    const rawProblemLevel = problem.level ? String(problem.level).toLowerCase() : '';
    const isExpert = rawConfigLevel === '3' || rawConfigLevel === 'expert' || rawConfigLevel === 'genio' ||
        rawProblemLevel === '3' || rawProblemLevel === 'expert' || rawProblemLevel === 'genio';

    // D Header Container
    const dHeaderContainer = document.createElement('div');
    dHeaderContainer.style.cssText = 'display: flex; flex-direction: column; align-items: center; position: relative;';

    const dHeader = document.createElement('div');
    dHeader.style.cssText = `${headerStyle} ${styleTens}`;
    dHeader.textContent = 'D';
    dHeaderContainer.appendChild(dHeader);

    // U Header Container (to match vertical alignment)
    const uHeaderContainer = document.createElement('div');
    uHeaderContainer.style.cssText = 'display: flex; flex-direction: column; align-items: center; position: relative;';

    const uHeader = document.createElement('div');
    uHeader.style.cssText = `${headerStyle} ${styleUnits}`;
    uHeader.textContent = 'U';
    uHeaderContainer.appendChild(uHeader);

    if (isExpert && !isSub) {
        // EXPERT INTERACTIVE CIRCLE (ALWAYS VISIBLE IN EXPERT ADDITION)
        const circle = document.createElement('div');
        circle.style.cssText = `
            width: 36px; height: 36px;
            border: 2px dashed #94a3b8;
            border-radius: 50%;
            display: flex; align-items: center; justify-content: center;
            font-size: 1rem; font-weight: 800; cursor: pointer;
            margin-top: 4px; transition: all 0.2s;
            color: transparent; background: white;
            user-select: none;
        `;
        circle.className = 'expert-carry-circle';
        circle.dataset.active = 'false'; // Track state
        circle.textContent = ''; // Ensure empty initial text

        // Accessibility Attributes
        circle.setAttribute('tabindex', '0');
        circle.setAttribute('role', 'button');
        circle.setAttribute('aria-label', 'Marcar llevada (+1)');

        // Keydown Handler for Enter/Space
        circle.onkeydown = (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                circle.click();
            }
        };

        circle.onclick = () => {
            const isActive = circle.dataset.active === 'true';

            if (isActive) {
                // Desactivar: ocultar el +1
                circle.dataset.active = 'false';
                circle.textContent = '';
                circle.style.color = 'transparent';
                circle.style.background = 'white';
                circle.style.borderColor = '#94a3b8';
                circle.style.borderStyle = 'dashed';
                circle.style.boxShadow = 'none';
                console.log('✨ Llevada desactivada por el usuario');
            } else {
                // Activar: mostrar el +1
                circle.dataset.active = 'true';
                circle.textContent = '+1';
                circle.style.color = 'white';
                circle.style.background = '#dc2626';
                circle.style.borderColor = '#dc2626';
                circle.style.borderStyle = 'solid';
                circle.style.boxShadow = '0 2px 8px rgba(220, 38, 38, 0.3)';
                console.log('✨ Llevada activada por el usuario');
                if (window.playPop) window.playPop();
            }
        };
        dHeaderContainer.appendChild(circle);

        // Spacer for U alignment
        const spacer = document.createElement('div');
        spacer.style.cssText = 'width: 36px; height: 36px; margin-top: 4px;';
        uHeaderContainer.appendChild(spacer);
    } else if (hasCarry) {
        // AUTO INDICATOR (Legacy/Basic/Inter)
        const carryIndicator = document.createElement('div');
        carryIndicator.style.cssText = `
            width: 32px; height: 32px;
            border-radius: 50%; background: #dc2626; color: white;
            display: flex; align-items: center; justify-content: center;
            font-size: 0.9rem; font-weight: 700; margin-top: 4px;
            box-shadow: 0 2px 8px rgba(220, 38, 38, 0.3);
        `;
        carryIndicator.textContent = '+1';
        dHeaderContainer.appendChild(carryIndicator);

        // Spacer for U alignment
        const spacer = document.createElement('div');
        spacer.style.cssText = 'width: 32px; height: 32px; margin-top: 4px;';
        uHeaderContainer.appendChild(spacer);
    }

    // HEADERS Row in Grid: Empty | Tens Header | Units Header
    gridHTML += `<div></div>`;
    gridHTML += `<div></div>`; // Placeholder for D
    gridHTML += `<div></div>`; // Placeholder for U

    // ROW 1: Empty | Tens1 | Units1
    gridHTML += `<div></div>`;
    if (getTens(num1) > 0) {
        gridHTML += `<div style="${dashedCellStyle} ${styleTens}">${getTens(num1)}</div>`;
    } else {
        gridHTML += `<div style="${dashedCellStyle} border: 2px dashed #cbd5e1;"></div>`;
    }
    gridHTML += `<div style="${dashedCellStyle} ${styleUnits}">${getUnits(num1)}</div>`;

    // ROW 2: Operator | Tens2 | Units2
    gridHTML += `<div style="font-size: 2.5rem; font-weight: 900; color: #1e293b;">${isSub ? '-' : '+'}</div>`;
    if (getTens(num2) > 0) {
        gridHTML += `<div style="${dashedCellStyle} ${styleTens}">${getTens(num2)}</div>`;
    } else {
        gridHTML += `<div style="${dashedCellStyle} border: 2px dashed #cbd5e1;"></div>`;
    }
    gridHTML += `<div style="${dashedCellStyle} ${styleUnits}">${getUnits(num2)}</div>`;

    // SEPARATOR
    gridHTML += `<div style="grid-column: span 3; height: 2px; background: #000; margin: 5px 0;"></div>`;

    // INPUT Row
    gridHTML += `<div></div>`;

    // Apply HTML to gridContainer
    safeSetHTML(gridContainer, gridHTML);

    // Insert headers into grid
    const children = Array.from(gridContainer.children);

    // Replace Tens Indicator Placeholder
    gridContainer.insertBefore(dHeaderContainer, children[1]);
    children[1].remove();

    // Replace Units Header Placeholder
    gridContainer.insertBefore(uHeaderContainer, children[2]);
    children[2].remove();

    // Tens Input (Red)
    const tInputContainer = document.createElement('div');
    tInputContainer.style.cssText = `${dashedCellStyle} padding: 0; overflow: hidden; border-color: #be123c;`;
    safeSetHTML(tInputContainer, `
        <input type="number" id="tensInput" class="calc-input-simple" placeholder="" min="0" max="49"
        oninput="if(parseInt(this.value)>49) this.value='49'; if(parseInt(this.value)<0) this.value='0'; this.style.fontSize = this.value.length > 1 ? '1.5rem' : '2rem';"
        style="width: 100%; height: 100%; border: none; text-align: center; font-size: 2rem; font-weight: 800; color: #be123c !important; outline: none; background: transparent;">
    `);
    gridContainer.appendChild(tInputContainer);

    // Units Input (Blue)
    const uInputContainer = document.createElement('div');
    uInputContainer.style.cssText = `${dashedCellStyle} padding: 0; overflow: hidden; border-color: #1e40af;`;
    safeSetHTML(uInputContainer, `
        <input type="number" id="unitsInput" class="calc-input-simple" placeholder="" min="0" max="49"
        oninput="if(parseInt(this.value)>49) this.value='49'; if(parseInt(this.value)<0) this.value='0'; this.style.fontSize = this.value.length > 1 ? '1.5rem' : '2rem';"
        style="width: 100%; height: 100%; border: none; text-align: center; font-size: 2rem; font-weight: 800; color: #1e40af !important; outline: none; background: transparent;">
    `);
    gridContainer.appendChild(uInputContainer);

    table.appendChild(gridContainer);
}

// === ROBUST SOLUTION HELPER ===
function getRobustSolution(problem) {
    if (!problem) return null;

    // 1. Try to calculate it fresh from data (Priority: Matches the visual table)
    if (problem.data && problem.data.useful && problem.data.useful.length >= 2) {
        const n1 = Math.max(...problem.data.useful);
        const n2 = Math.min(...problem.data.useful);

        // Robust operation check
        const isSub = isSubtraction(problem);

        const calculated = isSub ? n1 - n2 : n1 + n2;
        return calculated;
    }

    // 2. Fallback to stored solution
    if (problem.solution) {
        if (problem.solution.answer !== undefined) return parseInt(problem.solution.answer, 10);
        if (typeof problem.solution === 'number') return problem.solution;
        if (typeof problem.solution === 'string') return parseInt(problem.solution, 10);
    }

    return null;
}

export function checkCalculationTable() {
    const problem = store.state.problems[store.state.currentProblem];
    const tInput = document.getElementById('tensInput');
    const uInput = document.getElementById('unitsInput');

    if (!tInput || !uInput) return;

    // Limpieza estricta de inputs
    const cleanT = tInput.value.trim();
    const cleanU = uInput.value.trim();

    // Validar Unidades (Obligatorio)
    if (cleanU === '') {
        window.showWizardFeedback(getString('feedbackSelectResult') || 'Escribe las unidades', false);
        return;
    }

    // Validar Decenas (Opcional, asume 0 si vacío)
    const tVal = cleanT === '' ? 0 : parseInt(cleanT, 10);
    const uVal = parseInt(cleanU, 10);

    // Validar que sean números
    if (isNaN(tVal) || isNaN(uVal)) {
        window.showWizardFeedback('Por favor, escribe números válidos.', false);
        return;
    }

    // Validar no negativos
    if (tVal < 0 || uVal < 0) {
        window.showWizardFeedback('Los números no pueden ser negativos.', false);
        return;
    }

    // Validar límite máximo de 49 en decenas
    if (tVal > 49) {
        window.showWizardFeedback('El valor máximo permitido en las decenas es 49.', false);
        return;
    }

    // VALIDACIÓN EDUCATIVA: Unidades > 9
    if (uVal > 9) {
        window.showWizardFeedback('¡Recuerda! En las Unidades (U) solo cabe una cifra (0-9). Si la suma de las unidades es igual o mayor que 10, deberás sumar una decena en la columna D.', false);
        // Highlight unit input to help user
        uInput.style.border = '3px solid #dc2626';
        uInput.classList.add('animate__animated', 'animate__shakeX');
        setTimeout(() => {
            uInput.style.border = 'none';
            uInput.classList.remove('animate__animated', 'animate__shakeX');
        }, 2000);
        return;
    }

    // EXPERT MODE: Validar uso correcto del círculo +1
    const circle = document.querySelector('.expert-carry-circle');
    const num1 = problem.data && problem.data.useful ? Math.max(...problem.data.useful) : 0;
    const num2 = problem.data && problem.data.useful ? Math.min(...problem.data.useful) : 0;
    const getTens = n => Math.floor(n / 10);
    const getUnits = n => n % 10;
    const unitsSum = getUnits(num1) + getUnits(num2);
    const hasCarry = unitsSum >= 10;
    const isSub = isSubtraction(problem);

    if (circle) {
        const isCircleActive = circle.dataset.active === 'true';

        // Si el círculo está activo pero la operación NO tiene llevadas
        if (isCircleActive && !hasCarry) {
            window.showWizardFeedback('¡Atención! Has añadido una decena (+1) sin que la suma de las unidades sea igual o mayor que 10, por lo que deberá desaparecer en esta operación. Elimínalo pulsando el círculo antes de enviar la respuesta.', false);
            return;
        }

        // Si hay llevadas, el resultado es correcto, pero no ha marcado el +1
        if (hasCarry && !isCircleActive && !isSub) {
            const userResult = (tVal * 10) + uVal;
            const correctAnswer = num1 + num2;

            if (userResult === correctAnswer) {
                window.showWizardFeedback('¡Casi! El resultado es correcto, pero necesitas añadir el +1 en la columna de decenas. Púlsalo y vuelve a enviar la respuesta.', false);

                // Resaltar el círculo para ayudar al niño
                circle.style.borderColor = '#fbbf24'; // Amber highlight
                circle.style.boxShadow = '0 0 15px #fbbf24';
                circle.classList.add('animate__animated', 'animate__pulse');
                setTimeout(() => {
                    circle.style.borderColor = '#94a3b8';
                    circle.style.boxShadow = 'none';
                    circle.classList.remove('animate__animated', 'animate__pulse');
                }, 2500);
                return;
            }
        }
    }

    const userResult = (tVal * 10) + uVal;

    // --- VERIFICACIÓN MULTI-FUENTE ---
    const expectedCalc = getRobustSolution(problem);
    let expectedStored = null;

    // Intentar obtener la solución "oficial" almacenada por si el cálculo al vuelo falló
    if (problem.solution) {
        if (problem.solution.answer !== undefined) expectedStored = parseInt(problem.solution.answer, 10);
        else if (typeof problem.solution === 'number') expectedStored = problem.solution;
    }

    console.log('[CheckTable Debug] User:', userResult, 'Calculated:', expectedCalc, 'Stored:', expectedStored);

    let isCorrect = false;

    // 1. Coincide con el cálculo visual (Prioridad)
    if (userResult === expectedCalc) {
        isCorrect = true;
    }
    // 2. Coincide con la solución guardada (Red de seguridad)
    else if (expectedStored !== null && userResult === expectedStored) {
        console.warn('⚠️ User answer matched stored solution but NOT the calculated one. Accepting as correct.');
        isCorrect = true;
    }

    if (isCorrect) {
        wizardState.data.calculation = parseInt(userResult);
        window.showWizardFeedback(getString('feedbackCalcCorrect'), true, true);
        window.markStepComplete(5);
        setTimeout(() => window.routeNextWizardStep(5), 1500);
    } else {
        // Always show generic error message (no specific hints)
        window.showWizardFeedback(getString('feedbackCalcWrong') || '¡Muy cerca! Solo te has equivocado por 1. Cuenta con cuidado al último paso.', false);
    }
}

// === METHOD 3: CÁLCULO MENTAL ===
export function adjustCalcNumber(delta) {
    const display = document.getElementById('selectedCalcNumberDisplay');
    if (!display) return;

    // Inicializar si es la primera vez
    if (display.textContent === '?' || isNaN(currentMentalValue)) {
        currentMentalValue = 0;
    }

    currentMentalValue += delta;
    if (currentMentalValue < 0) currentMentalValue = 0; // No negativos
    if (currentMentalValue > 99) currentMentalValue = 99; // Límite máximo solicitado

    // CÁLCULO DINÁMICO DE TAMAÑO DE FUENTE
    // Si es > 9, reducimos fuente para que quepa bien en la caja
    if (currentMentalValue > 9) {
        display.style.fontSize = '2.5rem'; // Reducir un poco
        display.style.letterSpacing = '-2px'; // Juntar un poco
    } else {
        display.style.fontSize = ''; // Restaurar default (que suele ser 3rem o 4rem)
        display.style.letterSpacing = '';
    }

    const colorize = (n) => {
        const str = n.toString();
        if (str.length === 1) return `<span style="color: #1e40af;">${str}</span>`;
        const divTens = `<span style="color: #be123c;">${str.slice(0, -1)}</span>`;
        const divUnits = `<span style="color: #1e40af;">${str.slice(-1)}</span>`;
        return divTens + divUnits;
    };

    safeSetHTML(display, colorize(currentMentalValue));

    // Feedback sonoro
    if (window.playPop) window.playPop();

    const btn = document.getElementById('validateCalcBtn');
    if (btn) btn.disabled = false;
}

export function validateCalculation() {
    const problem = store.state.problems[store.state.currentProblem];
    const expected = getRobustSolution(problem);

    console.log('[CheckMental] User:', currentMentalValue, 'Expected:', expected);

    if (currentMentalValue === expected) {
        wizardState.data.calculation = parseInt(currentMentalValue);
        window.showWizardFeedback(getString('feedbackCalcCorrect'), true, true);
        window.markStepComplete(5);
        setTimeout(() => {
            window.routeNextWizardStep(5);
        }, 1500);
    } else {
        // Always show generic error message (no specific hints)
        window.showWizardFeedback(getString('feedbackCalcWrong') || 'Revisa tu cálculo. ¡Inténtalo de nuevo!', false);
    }
}

// === EXISTING HANDLERS (for compatibility) ===

// Handler para input directo (teclado) - Mantenido por si acaso
export function checkCalculation() {
    const problem = store.state.problems[store.state.currentProblem];
    const userVal = parseFloat(document.getElementById('finalResultInput')?.value);

    if (isNaN(userVal)) return;

    const expected = getRobustSolution(problem);

    if (userVal === expected) {
        calculationTableComplete = true;
        wizardState.data.calculation = userVal;
        window.markStepComplete(5);
    }
}

// Handler para selección desde Tabla 1-100 (Legacy internal use if needed)
export function handleNumberSelection(number) {
    const display = document.getElementById('selectedNumberDisplay');
    if (display) {
        display.textContent = number;
        display.dataset.value = number;
        display.classList.add('updated');
        setTimeout(() => display.classList.remove('updated'), 300);
    }
    closeNumberTable();

    const problem = store.state.problems[store.state.currentProblem];
    const expected = getRobustSolution(problem);

    if (parseInt(number) === expected) {
        numberSelectorComplete = true;
        wizardState.data.calculation = parseInt(number); // Save the value!
        if (display) display.style.color = '#10B981';

        window.showWizardFeedback(getString('feedbackCalcCorrect'), true, true);
        window.markStepComplete(5);
        setTimeout(() => window.routeNextWizardStep(5), 1500);
    } else {
        window.showWizardFeedback(getString('feedbackCalcWrong'), false);
    }
}

window.checkCalculation = function () {
    const problem = store.state.problems[store.state.currentProblem];
    const method = store.config ? store.config.method : 'abn';

    // 1. Get User Answer
    let userAnswer = null;

    if (method === 'abn') {
        // Method 2: ABN Grid
        // ... (ABN implementation details if needed) ...
        const input = document.getElementById('finalResultInput');
        if (input) userAnswer = parseInt(input.value);
    } else if (method === 'mental') {
        // Method 3: Mental Calc (Spinner)
        const display = document.getElementById('selectedCalcNumberDisplay');
        if (display) userAnswer = parseInt(display.textContent);
    } else {
        // Method 1: Vertical Grid
        // Combine inputs: Tens * 10 + Units
        const tInput = document.getElementById('calcTensInput');
        const uInput = document.getElementById('calcUnitsInput');
        if (tInput && uInput) {
            const tens = parseInt(tInput.textContent || tInput.value || '0');
            const units = parseInt(uInput.textContent || uInput.value || '0');
            // If inputs are empty/NaN, treat as incomplete
            if (isNaN(tens) || isNaN(units)) {
                window.showWizardFeedback(getString('feedbackUseDataMissing'), false);
                return;
            }
            userAnswer = tens * 10 + units;

            // --- CARRY OVER VALIDATION (Feature 2) ---
            if (!isSubtraction(problem)) {
                let num1 = 0;
                let num2 = 0;
                if (problem.data && problem.data.useful) {
                    num1 = Math.max(...problem.data.useful);
                    num2 = Math.min(...problem.data.useful);
                }
                const unitsSum = (num1 % 10) + (num2 % 10);
                const hasCarry = unitsSum >= 10;

                // Expert check logic (reused from render)
                const rawConfigLevel = store.config ? String(store.config.level).toLowerCase() : '';
                const rawProblemLevel = problem.level ? String(problem.level).toLowerCase() : '';
                const isExpert = rawConfigLevel === '3' || rawConfigLevel === 'expert' || rawConfigLevel === 'genio' ||
                    rawProblemLevel === '3' || rawProblemLevel === 'expert' || rawProblemLevel === 'genio';

                if (isExpert && hasCarry) {
                    // Find the circle
                    const circle = document.querySelector('.expert-carry-circle');
                    const isCarryMarked = circle && circle.dataset.active === 'true';

                    if (!isCarryMarked) {
                        // Correct result but forgot carry over
                        const correctSum = num1 + num2;
                        if (userAnswer === correctSum) {
                            window.showWizardFeedback("¡Casi! El resultado es correcto, pero olvidaste marcar la llevada (+1). Colócala en las decenas.", false);

                            // Highlight the circle to help
                            if (circle) {
                                circle.style.borderColor = '#fbbf24'; // Amber highlight
                                circle.style.boxShadow = '0 0 15px #fbbf24';
                                setTimeout(() => {
                                    circle.style.borderColor = '#94a3b8';
                                    circle.style.boxShadow = 'none';
                                }, 2000);
                            }
                            return;
                        }
                    }
                }
            }
            // ------------------------------------------

        } else {
            // Fallback for missing inputs
            const input = document.getElementById('finalResultInput');
            if (input) userAnswer = parseInt(input.value);
        }
    }

    if (userAnswer === null || isNaN(userAnswer)) {
        window.showWizardFeedback(getString('feedbackUseDataMissing'), false);
        return;
    }

    // 2. Validate
    let correctAnswer = 0;
    if (problem.data && problem.data.useful) {
        const num1 = Math.max(...problem.data.useful);
        const num2 = Math.min(...problem.data.useful);
        if (isSubtraction(problem)) {
            correctAnswer = num1 - num2;
        } else {
            correctAnswer = num1 + num2;
        }
    }

    console.log(`🧮 Validando: Usuario=${userAnswer}, Correcto=${correctAnswer}`);

    if (userAnswer === correctAnswer) {
        // Save answer
        wizardState.data.calculation = userAnswer;
        window.showWizardFeedback(getString('feedbackVeryGood'), true, true);
        window.markStepComplete(5);
        setTimeout(() => {
            window.routeNextWizardStep(5);
        }, 1500);
    } else {
        window.showWizardFeedback(getString('feedbackCalcError'), false);
    }
};

// ABN Helper Wrappers (Method 2)
export function openNumberTable() {
    if (window.openAbnHelper) {
        window.openAbnHelper(); // Call the global function!
    } else {
        console.error("ABN Helper not found");
    }
}

export function closeNumberTable() {
    const modal = document.getElementById('numberTableModal'); // Legacy modal
    if (modal) modal.classList.add('hidden');
}

// Expose to global scope for ABN Helper callback
window.handleNumberSelection = handleNumberSelection;

// --- METHOD SELECTION ---
export function selectCalcMethod(methodId) {
    const selector = document.getElementById('calculationMethodSelector');
    if (selector) selector.classList.add('hidden');
    
    const container = document.getElementById('calculationMethodsContainer');
    if (container) container.classList.remove('hidden');
    
    // Hide all methods
    document.querySelectorAll('.calculation-method').forEach(el => el.classList.add('hidden'));
    
    // Show selected method
    const target = document.getElementById(`calcMethod${methodId}`);
    if (target) {
        target.classList.remove('hidden');
        
        // Specific init for Method 1 (Table)
        if (methodId === 1) {
            const tableContainer = document.getElementById('calcTable');
            if (tableContainer) {
                tableContainer.innerHTML = '';
                renderVerticalCalcTable(tableContainer);
            }
        }
        
        // Novedad: Recargar botones de voz para las instrucciones de los métodos
        if (window.addSafeAudioButtons) {
            setTimeout(window.addSafeAudioButtons, 100);
        }
    }
}

export function backToMethodSelector() {
    const container = document.getElementById('calculationMethodsContainer');
    if (container) container.classList.add('hidden');
    
    document.querySelectorAll('.calculation-method').forEach(el => el.classList.add('hidden'));
    
    const selector = document.getElementById('calculationMethodSelector');
    if (selector) selector.classList.remove('hidden');
}

window.selectCalcMethod = selectCalcMethod;
window.backToMethodSelector = backToMethodSelector;
